import { useEffect, useRef } from "react";
import { useAccount } from "wagmi";

import { ShipTier, ShipTierColors, ShipTierNames } from "@voidex/contracts-ts";
import {
	useApproveUsdcForShop,
	useBuyShip,
	useShipsByTier,
	useUsdcAllowanceForShop,
	useUsdcBalance,
} from "../../hooks";
import { useTransactionStore } from "../../stores/transactionStore";
import { LoadingButton, ShipIcon } from "../shared";

interface ShipBuyCardProps {
	tier: ShipTier;
	price: bigint;
	formattedPrice: string;
}

function shortError(err: unknown): string {
	const msg = err instanceof Error ? err.message : String(err);
	if (msg.includes("User rejected")) return "Transaction rejected";
	if (msg.includes("insufficient funds")) return "Insufficient ETH for gas";
	if (msg.includes("exceeds balance")) return "Insufficient USDC balance";
	return msg.length > 120 ? `${msg.slice(0, 120)}...` : msg;
}

const TIER_MULTIPLIER: Record<ShipTier, string> = {
	[ShipTier.Probe]: "1x",
	[ShipTier.Frigate]: "1.5x",
	[ShipTier.Dreadnought]: "2.5x",
};

const TIER_RISK: Record<ShipTier, string> = {
	[ShipTier.Probe]: "~5%",
	[ShipTier.Frigate]: "~15%",
	[ShipTier.Dreadnought]: "~30%",
};

export function ShipBuyCard({ tier, price, formattedPrice }: ShipBuyCardProps) {
	const { address } = useAccount();
	const {
		allowance,
		isLoading: isAllowanceLoading,
		refetch: refetchAllowance,
	} = useUsdcAllowanceForShop(address);
	const {
		approve,
		isPending: isApproving,
		isSuccess: isApproveSuccess,
		reset: resetApprove,
	} = useApproveUsdcForShop();
	const { buyShip, isPending: isBuying, isSuccess: isBuySuccess, reset: resetBuy } = useBuyShip();
	const { refetch: refetchInventory } = useShipsByTier(address);
	const { refetch: refetchUsdcBalance } = useUsdcBalance(address);
	const { addToast } = useTransactionStore();

	const tierName = ShipTierNames[tier];
	const tierColor = ShipTierColors[tier];
	// After approval confirms, the RPC may return stale allowance for a few seconds.
	// Track approval success locally so the Buy button shows immediately.
	const approvedRef = useRef(false);
	if (isApproveSuccess) approvedRef.current = true;
	// Reset after a successful buy (allowance was spent)
	if (isBuySuccess) approvedRef.current = false;

	const needsApproval = price > 0n && !approvedRef.current && !isAllowanceLoading && allowance < price;

	const approveToastIdRef = useRef<string | null>(null);
	const buyToastIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (!isApproveSuccess) return;

		if (approveToastIdRef.current) {
			useTransactionStore.getState().updateToast(approveToastIdRef.current, {
				type: "success",
				title: "USDC Approved",
				message: `Allowance set for ${tierName} purchase`,
			});
			approveToastIdRef.current = null;
		}

		// Refetch immediately + retry after delay for RPC propagation
		refetchAllowance();
		const timer = setTimeout(() => refetchAllowance(), 3000);
		return () => clearTimeout(timer);
	}, [isApproveSuccess, tierName, refetchAllowance]);

	useEffect(() => {
		if (!isBuySuccess) return;

		if (buyToastIdRef.current) {
			useTransactionStore.getState().updateToast(buyToastIdRef.current, {
				type: "success",
				title: `${tierName} Acquired!`,
				message: "New ship added to your fleet",
			});
			buyToastIdRef.current = null;
		}

		// Immediate refetch for on-chain data
		refetchAllowance();
		refetchUsdcBalance();

		// Delayed refetch for subgraph inventory (indexer lag)
		const timer = setTimeout(() => refetchInventory(), 5000);
		return () => clearTimeout(timer);
	}, [isBuySuccess, tierName, refetchInventory, refetchAllowance, refetchUsdcBalance]);

	async function handleApprove(): Promise<void> {
		resetApprove();
		const toastId = addToast({
			type: "pending",
			title: "Approving USDC",
			message: "One-time approval for USDC spending",
		});
		approveToastIdRef.current = toastId;
		try {
			await approve();
		} catch (err) {
			console.error("[ShipBuyCard] approve failed:", err);
			useTransactionStore.getState().updateToast(toastId, {
				type: "error",
				title: "Approval Failed",
				message: shortError(err),
			});
			approveToastIdRef.current = null;
		}
	}

	async function handleBuy(): Promise<void> {
		resetBuy();
		const toastId = addToast({
			type: "pending",
			title: price === 0n ? `Minting ${tierName}` : `Buying ${tierName}`,
			message: price === 0n ? "Free mint in progress..." : `Purchasing for ${formattedPrice} USDC...`,
		});
		buyToastIdRef.current = toastId;
		try {
			await buyShip(tier);
		} catch (err) {
			console.error("[ShipBuyCard] buyShip failed:", err);
			useTransactionStore.getState().updateToast(toastId, {
				type: "error",
				title: "Purchase Failed",
				message: shortError(err),
			});
			buyToastIdRef.current = null;
		}
	}

	return (
		<div className="ship-showcase" style={{ "--showcase-color": tierColor } as React.CSSProperties}>
			<div className="ship-showcase__display">
				<ShipIcon tier={tier} size="xl" />
			</div>

			<h3 className="ship-showcase__name">{tierName}</h3>

			<div className="ship-showcase__specs">
				<div className="ship-showcase__spec">
					<span className="ship-showcase__spec-label">Price</span>
					<span className="ship-showcase__spec-value ship-showcase__spec-value--price">
						{price === 0n ? "FREE" : `${formattedPrice} USDC`}
					</span>
				</div>
				<div className="ship-showcase__spec">
					<span className="ship-showcase__spec-label">Multiplier</span>
					<span className="ship-showcase__spec-value">{TIER_MULTIPLIER[tier]}</span>
				</div>
				<div className="ship-showcase__spec">
					<span className="ship-showcase__spec-label">Risk</span>
					<span className="ship-showcase__spec-value">{TIER_RISK[tier]}</span>
				</div>
			</div>

			<div className="ship-showcase__action">
				{needsApproval ? (
					<LoadingButton
						onClick={handleApprove}
						loading={isApproving}
						variant="secondary"
					>
						Approve USDC
					</LoadingButton>
				) : (
					<LoadingButton
						onClick={handleBuy}
						loading={isBuying || (price > 0n && isAllowanceLoading)}
						disabled={price > 0n && isAllowanceLoading}
					>
						{price === 0n ? `Mint ${tierName}` : `Buy ${tierName}`}
					</LoadingButton>
				)}
			</div>
		</div>
	);
}
